// src/scripts/googleLogin.js

import {
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

import { auth } from "@/firebase";
import { storeUserContext } from "@/scripts/userContext";

/**
 * Build the NORMAL Google authentication provider.
 *
 * IMPORTANT:
 *
 * Do NOT force:
 *
 *   prompt: "consent"
 *   prompt: "select_account"
 *
 * Firebase's GoogleAuthProvider already requests the standard
 * Google profile/email information required for Firebase Authentication.
 *
 * This allows an account that has already authorised CoreDeck to reuse
 * that existing Google authorisation rather than repeatedly presenting
 * a fresh consent screen.
 */
function buildGoogleProvider() {
  return new GoogleAuthProvider();
}

/**
 * Build the provider used ONLY when the user explicitly chooses
 * "Switch account".
 *
 * This deliberately asks Google to show the account chooser.
 *
 * It does NOT force consent.
 */
function buildGoogleSwitchAccountProvider() {
  const provider =
    new GoogleAuthProvider();

  provider.setCustomParameters({
    prompt: "select_account",
  });

  return provider;
}

/**
 * Complete a Google -> Firebase -> CoreDeck login.
 */
async function performGoogleLogin(
  provider,
  logLabel
) {
  try {
    console.log(
      `[GOOGLE LOGIN] Starting ${logLabel}`
    );

    const result =
      await signInWithPopup(
        auth,
        provider
      );

    if (
      !result ||
      !result.user ||
      !result.user.uid
    ) {
      throw new Error(
        "Google authentication completed without returning a Firebase user."
      );
    }

    console.log(
      "[GOOGLE LOGIN] Firebase authentication successful",
      {
        uid:
          result.user.uid,

        email:
          result.user.email ||
          "",
      }
    );

    /*
     * Load CoreDeck user context only after Firebase authentication
     * has completed successfully.
     */
    await storeUserContext(
      result.user.uid
    );

    console.log(
      "[GOOGLE LOGIN] CoreDeck user context loaded"
    );

    return result;
  } catch (error) {
    console.error(
      "[GOOGLE LOGIN ERROR]",
      {
        code:
          error?.code ||
          "",

        message:
          error?.message ||
          "",

        customData:
          error?.customData ||
          null,

        raw:
          error,
      }
    );

    /*
     * Preserve the real Firebase error.
     */
    throw error;
  }
}

/**
 * NORMAL GOOGLE LOGIN
 *
 * Used by:
 *
 *   Continue with Google
 *
 * Does NOT force:
 *
 * - account selection
 * - fresh Google consent
 *
 * An account that has previously authorised CoreDeck should therefore
 * reuse that existing Google authorisation.
 */
export async function loginWithGoogle() {
  const provider =
    buildGoogleProvider();

  return performGoogleLogin(
    provider,
    "normal Google authentication"
  );
}

/**
 * SWITCH GOOGLE ACCOUNT
 *
 * Used ONLY by:
 *
 *   Switch account
 *
 * This explicitly asks Google to display the account chooser.
 */
export async function loginWithGoogleSelectAccount() {
  const provider =
    buildGoogleSwitchAccountProvider();

  return performGoogleLogin(
    provider,
    "Google account switch authentication"
  );
}

/**
 * Compatibility export.
 *
 * CoreDeck Google login is popup-only.
 * There is no redirect result to process.
 */
export async function handleGoogleRedirectResult() {
  return null;
}